import { CircleDollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AlertChip } from '@/components/atoms/AlertChip';
import { TraceIdBadge } from '@/components/atoms/TraceIdBadge';
import { VariantBadge } from '@/components/atoms/VariantBadge';
import { ResultPill } from '@/components/molecules/ResultPill';
import { modulePath } from '@/config/routes';
import { useUsd } from '@/hooks/useUsd';
import { formatDateTime } from '@/utils/datetime';
import { decamelizeKey } from '@/utils/decamelize';

import { STATUS_STYLE } from './ThreadSidebar.constants';
import type { ThreadSidebarProps } from './ThreadSidebar.types';

const Block = ({ children }: { children: React.ReactNode }) => (
	<section className="flex flex-col gap-2xs border-b border-subtle px-md py-sm last:border-b-0">{children}</section>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
	<div className="flex flex-col gap-3xs">
		<dt className="text-[10px] font-medium uppercase tracking-wider text-subtle">{label}</dt>
		{/* `break-all` on purpose: a 24-char ObjectId in a 320px rail is exactly
		    what used to paint over the next column in the old header grid. */}
		<dd className="break-all text-[12px] font-medium leading-tight text-default">{children}</dd>
	</div>
);

export const ThreadSidebar = ({
	conversation: conv,
	turns,
	variants,
	onEvaluationsClick,
	traceId,
}: ThreadSidebarProps) => {
	const usd = useUsd();
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const status = STATUS_STYLE[conv.status];
	const variantEntries = Object.entries(variants).map(([k, v]) => [decamelizeKey(k), v] as const);
	const evaluation = conv.evaluationSummary;

	return (
		<aside
			aria-label={t('SIDEBAR_TITLE')}
			className="h-fit rounded-lg border border-subtle bg-white lg:sticky lg:top-lg"
		>
			<Block>
				<Field label={t('STATUS_LABEL')}>
					<span
						className={`inline-flex items-center rounded-md border px-2xs py-3xs text-[11px] font-medium leading-none ${status.className}`}
					>
						{t(status.labelKey)}
					</span>
				</Field>
				<Field label={t('RESULT_LABEL')}>
					<ResultPill result={conv.result} />
				</Field>
				<div className="mt-2xs flex flex-col gap-3xs">
					<p className="text-[10px] font-medium uppercase tracking-wider text-subtle">{t('ALERTS_LABEL')}</p>
					{conv.alerts.length === 0 ? (
						<p className="text-[12px] text-subtle">{t('ALERTS_EMPTY')}</p>
					) : (
						<div className="flex flex-wrap gap-3xs">
							{conv.alerts.map((alert) => (
								<AlertChip key={alert} alert={alert} />
							))}
						</div>
					)}
				</div>
			</Block>

			<Block>
				<p className="flex items-center gap-3xs text-[10px] font-medium uppercase tracking-wider text-purple-700">
					<CircleDollarSign className="h-sm w-sm" />
					{t('THREAD_COST')}
				</p>
				<p className="text-[22px] font-semibold leading-none tabular-nums text-purple-700">{usd.unit(conv.costUsd)}</p>
				<p className="text-[11px] tabular-nums text-subtle">
					{t('TOKENS', { in: conv.tokensIn, out: conv.tokensOut, tools: conv.toolCallCount })}
				</p>
			</Block>

			<Block>
				<Field label={t('CONSULTATION_AREAS')}>
					{conv.consultationAreas.length === 0 ? (
						<span className="text-subtle">—</span>
					) : (
						<span className="flex flex-wrap gap-3xs">
							{conv.consultationAreas.map((area) => (
								<span key={area} className="rounded-md bg-grey-25 px-2xs py-3xs text-[11px] font-normal text-subtle">
									{area}
								</span>
							))}
						</span>
					)}
				</Field>
				{evaluation && (
					<button
						type="button"
						onClick={onEvaluationsClick}
						className="mt-2xs flex flex-col gap-3xs text-left transition-colors hover:text-purple-700"
					>
						<span className="text-[10px] font-medium uppercase tracking-wider text-subtle">
							{t('EVALUATIONS_RAIL_LABEL')}
						</span>
						<span className={`text-[12px] font-medium ${evaluation.hasFailures ? 'text-error' : 'text-default'}`}>
							{t('EVALUATIONS_SUMMARY', { passed: evaluation.passedJudges, total: evaluation.totalJudges })}
						</span>
					</button>
				)}
			</Block>

			<Block>
				<dl className="flex flex-col gap-sm">
					<Field label={t('USER_ID')}>
						<span className="font-mono">{conv.userId}</span>
					</Field>
					<Field label={t('CHANNEL')}>{conv.channel}</Field>
					<Field label={t('COMPANY_ID')}>
						{conv.companyName} · <span className="font-mono">{conv.companyId}</span>
					</Field>
					<Field label={t('MODULE_AGENT')}>
						<Link to={modulePath(conv.moduleName)} className="transition-colors hover:text-purple-700">
							{conv.moduleName} / {conv.agentName}
						</Link>
					</Field>
					<Field label={t('STARTED')}>
						<span className="tabular-nums">{formatDateTime(conv.startedAt)}</span>
					</Field>
					<Field label={t('LAST_ACTIVITY')}>{conv.lastActivity}</Field>
					<Field label={t('TURNS')}>
						<span className="tabular-nums">{turns}</span>
					</Field>
				</dl>
			</Block>

			{traceId && (
				<Block>
					<p className="text-[10px] font-medium uppercase tracking-wider text-subtle">{t('TRACE_ID')}</p>
					<TraceIdBadge traceId={traceId} />
				</Block>
			)}

			{variantEntries.length > 0 && (
				<Block>
					<p className="text-[10px] font-medium uppercase tracking-wider text-subtle">{t('BOT_VERSION')}</p>
					<div className="flex flex-wrap gap-2xs">
						{variantEntries.map(([experiment, variant]) => (
							<VariantBadge key={experiment} experiment={experiment} variant={variant} />
						))}
					</div>
				</Block>
			)}
		</aside>
	);
};
